import React, { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";

export default function DocumentTypeMultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "Select document type",
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointer = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const toggleType = (type) => {
    if (value.includes(type)) {
      onChange(value.filter((item) => item !== type));
      return;
    }
    onChange([...value, type]);
  };

  const removeType = (type, event) => {
    event.preventDefault();
    event.stopPropagation();
    onChange(value.filter((item) => item !== type));
  };

  const visibleType = value[0];
  const overflowTypes = value.slice(1);
  const selectedSummary = value.join(", ");

  return (
    <div ref={wrapRef} style={styles.wrap}>
      <div
        role="combobox"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label="Document type"
        title={selectedSummary || undefined}
        style={styles.trigger}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((current) => !current);
          }
        }}
      >
        <div style={styles.chips}>
          {value.length === 0 ? (
            <span style={styles.placeholder}>{placeholder}</span>
          ) : (
            <>
              <span key={visibleType} style={styles.chip} title={visibleType}>
                <span style={styles.chipLabel}>{visibleType}</span>
                <button
                  type="button"
                  style={styles.chipRemove}
                  aria-label={`Remove ${visibleType}`}
                  onClick={(event) => removeType(visibleType, event)}
                >
                  <X size={10} strokeWidth={2.5} />
                </button>
              </span>
              {overflowTypes.length > 0 ? (
                <span
                  style={styles.overflowChip}
                  title={overflowTypes.join(", ")}
                  aria-label={`${overflowTypes.length} more document types`}
                >
                  +{overflowTypes.length}
                </span>
              ) : null}
            </>
          )}
        </div>
        <ChevronDown size={14} color="#64748b" aria-hidden="true" />
      </div>

      <div
        id={menuId}
        style={open ? styles.menu : styles.menuHidden}
        role="listbox"
        aria-multiselectable="true"
        hidden={!open}
      >
        {options.length === 0 ? (
          <div style={styles.empty}>No document types</div>
        ) : (
          options.map((type) => {
            const checked = value.includes(type);
            return (
              <label key={type} style={styles.option}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleType(type)}
                />
                <span>{type}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    position: "relative",
    width: "100%",
  },
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 34,
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0 8px 0 10px",
    background: "#fff",
    cursor: "pointer",
    outline: "none",
    overflow: "hidden",
  },
  chips: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: 6,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  placeholder: {
    color: "#94a3b8",
    fontSize: 12,
    lineHeight: "24px",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    minWidth: 0,
    maxWidth: "100%",
    flex: "0 1 auto",
    padding: "2px 6px",
    borderRadius: 999,
    background: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #93c5fd",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  overflowChip: {
    display: "inline-flex",
    alignItems: "center",
    flex: "0 0 auto",
    padding: "2px 7px",
    borderRadius: 999,
    background: "#f1f5f9",
    color: "#334155",
    border: "1px solid #cbd5e1",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  chipLabel: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chipRemove: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    padding: 0,
    border: 0,
    borderRadius: 999,
    background: "transparent",
    color: "#1d4ed8",
    cursor: "pointer",
  },
  menu: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    zIndex: 20,
    maxHeight: 240,
    overflowY: "auto",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
    padding: 6,
  },
  menuHidden: {
    display: "none",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px",
    borderRadius: 6,
    fontSize: 12,
    color: "#0f172a",
    cursor: "pointer",
  },
  empty: {
    padding: "10px 8px",
    fontSize: 12,
    color: "#64748b",
  },
};
